import { dtoolsSystemContentKeyAliases, resolveDtoolsSystemContentKey, systemContentKey } from "./dtoolsProposalContent";
import type { DtoolsReviewSystem } from "./dtoolsStagingTransform";

export type DtoolsSystemClassificationKind = "independent" | "alternative";

export type DtoolsSystemClassificationEntry = {
  classification: DtoolsSystemClassificationKind;
  groupName?: string | null;
  /** Frozen D-Tools import label — preserved when customer-facing name is edited. */
  originalSystemName?: string | null;
  /** Customer-facing override; blank falls back to originalSystemName. */
  customerFacingSystemName?: string | null;
};

export type DtoolsSystemClassificationsMap = Record<string, DtoolsSystemClassificationEntry>;

export const DTOOLS_TAB_MODEL_VERSION_LEGACY = 1 as const;
export const DTOOLS_TAB_MODEL_VERSION_CLASSIFICATION = 2 as const;
export type DtoolsTabModelVersion =
  | typeof DTOOLS_TAB_MODEL_VERSION_LEGACY
  | typeof DTOOLS_TAB_MODEL_VERSION_CLASSIFICATION;

export const DEFAULT_SYSTEM_CLASSIFICATION: DtoolsSystemClassificationEntry = {
  classification: "independent",
};

export function readDtoolsTabModelVersion(parsedMetadataJson: unknown): DtoolsTabModelVersion {
  if (!parsedMetadataJson || typeof parsedMetadataJson !== "object" || Array.isArray(parsedMetadataJson)) {
    return DTOOLS_TAB_MODEL_VERSION_LEGACY;
  }
  const raw = (parsedMetadataJson as Record<string, unknown>).dtoolsTabModelVersion;
  const n = Number(raw);
  if (n === DTOOLS_TAB_MODEL_VERSION_CLASSIFICATION) return DTOOLS_TAB_MODEL_VERSION_CLASSIFICATION;
  return DTOOLS_TAB_MODEL_VERSION_LEGACY;
}

function normalizeClassificationEntry(value: unknown): DtoolsSystemClassificationEntry | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  const classification = v.classification === "alternative" ? "alternative" : "independent";
  const groupNameRaw = v.groupName;
  const groupName =
    typeof groupNameRaw === "string"
      ? groupNameRaw.trim() || null
      : groupNameRaw === null
        ? null
        : undefined;
  const originalSystemName =
    typeof v.originalSystemName === "string"
      ? v.originalSystemName.trim() || null
      : v.originalSystemName === null
        ? null
        : undefined;
  const customerFacingSystemName =
    typeof v.customerFacingSystemName === "string"
      ? v.customerFacingSystemName
      : v.customerFacingSystemName === null
        ? null
        : undefined;
  return {
    classification,
    ...(groupName !== undefined ? { groupName } : {}),
    ...(originalSystemName !== undefined ? { originalSystemName } : {}),
    ...(customerFacingSystemName !== undefined ? { customerFacingSystemName } : {}),
  };
}

export function readDtoolsSystemClassifications(parsedMetadataJson: unknown): DtoolsSystemClassificationsMap {
  if (!parsedMetadataJson || typeof parsedMetadataJson !== "object" || Array.isArray(parsedMetadataJson)) {
    return {};
  }
  const raw = (parsedMetadataJson as Record<string, unknown>).dtoolsSystemClassifications;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: DtoolsSystemClassificationsMap = {};
  for (const [key, value] of Object.entries(raw)) {
    const entry = normalizeClassificationEntry(value);
    if (entry) out[key] = entry;
  }
  return out;
}

export function mergeDtoolsSystemClassifications(
  parsedMetadataJson: unknown,
  patch: DtoolsSystemClassificationsMap,
): Record<string, unknown> {
  const base =
    parsedMetadataJson && typeof parsedMetadataJson === "object" && !Array.isArray(parsedMetadataJson)
      ? { ...(parsedMetadataJson as Record<string, unknown>) }
      : {};
  const existing = readDtoolsSystemClassifications(base);
  return {
    ...base,
    dtoolsSystemClassifications: { ...existing, ...patch },
  };
}

export function mergeDtoolsTabModelVersion(
  parsedMetadataJson: unknown,
  version: DtoolsTabModelVersion,
): Record<string, unknown> {
  const base =
    !parsedMetadataJson || typeof parsedMetadataJson !== "object" || Array.isArray(parsedMetadataJson)
      ? {}
      : { ...(parsedMetadataJson as Record<string, unknown>) };
  return {
    ...base,
    dtoolsTabModelVersion: version,
  };
}

export function resolveSystemClassification(
  map: DtoolsSystemClassificationsMap | undefined,
  systemId: string | null | undefined,
  systemName: string,
): DtoolsSystemClassificationEntry {
  const system = { systemId, name: systemName };
  for (const key of dtoolsSystemContentKeyAliases(system)) {
    const entry = map?.[key];
    if (entry) return entry;
  }
  return DEFAULT_SYSTEM_CLASSIFICATION;
}

export function isAlternativeSystemClassification(entry: DtoolsSystemClassificationEntry | undefined): boolean {
  return entry?.classification === "alternative";
}

/** Ensure every active system has an explicit classification entry (defaults to independent). */
export function ensureSystemClassificationsForSystems(
  systems: Array<{ systemId?: string | null; name: string }>,
  existing: DtoolsSystemClassificationsMap | undefined,
  options?: { legacySystemDisplayNames?: Record<string, string> },
): DtoolsSystemClassificationsMap {
  const next: DtoolsSystemClassificationsMap = { ...(existing ?? {}) };
  const legacy = options?.legacySystemDisplayNames ?? {};
  for (const system of systems) {
    const key = resolveDtoolsSystemContentKey(system);
    const importedName = String(system.name ?? "").trim() || null;
    const prev = next[key];
    if (!prev) {
      const legacyName = legacy[key]?.trim() || null;
      next[key] = {
        ...DEFAULT_SYSTEM_CLASSIFICATION,
        originalSystemName: importedName,
        customerFacingSystemName: legacyName,
      };
      continue;
    }
    const merged: DtoolsSystemClassificationEntry = { ...prev };
    if (!merged.originalSystemName?.trim() && importedName) {
      merged.originalSystemName = importedName;
    }
    if (!merged.customerFacingSystemName?.trim() && legacy[key]?.trim()) {
      merged.customerFacingSystemName = legacy[key]!.trim();
    }
    next[key] = merged;
  }
  return next;
}

export function filterAlternativeSystems<T extends { systemId?: string | null; name: string }>(
  systems: T[],
  classifications: DtoolsSystemClassificationsMap,
): T[] {
  return systems.filter((system) =>
    isAlternativeSystemClassification(resolveSystemClassification(classifications, system.systemId, system.name)),
  );
}

/** Default customer-facing proposal tab label from the option / system name. */
export function resolveDefaultTabName(
  entry: Pick<DtoolsSystemClassificationEntry, "customerFacingSystemName" | "originalSystemName">,
  importedSystemName: string,
): string | null {
  const fromOption = entry.customerFacingSystemName?.trim();
  if (fromOption) return fromOption;
  const original = entry.originalSystemName?.trim() || String(importedSystemName ?? "").trim();
  return original || null;
}

export function patchSystemClassificationEntry(
  map: DtoolsSystemClassificationsMap,
  key: string,
  patch: Partial<DtoolsSystemClassificationEntry>,
): DtoolsSystemClassificationsMap {
  const prev = map[key] ?? { ...DEFAULT_SYSTEM_CLASSIFICATION };
  const classification = patch.classification ?? prev.classification;
  const hasGroupNamePatch = Object.prototype.hasOwnProperty.call(patch, "groupName");
  const mergedForDefault: DtoolsSystemClassificationEntry = {
    ...prev,
    classification,
    originalSystemName:
      patch.originalSystemName !== undefined
        ? patch.originalSystemName?.trim() || null
        : prev.originalSystemName ?? null,
    customerFacingSystemName:
      patch.customerFacingSystemName !== undefined
        ? patch.customerFacingSystemName === null
          ? null
          : patch.customerFacingSystemName
        : prev.customerFacingSystemName ?? null,
  };
  let groupName: string | null;
  if (classification === "independent") {
    groupName = null;
  } else if (hasGroupNamePatch) {
    groupName = patch.groupName === null ? null : String(patch.groupName ?? "");
  } else {
    groupName = prev.groupName ?? null;
  }
  if (classification === "alternative" && !hasGroupNamePatch && !String(groupName ?? "").trim()) {
    groupName = resolveDefaultTabName(mergedForDefault, "");
  }
  const next: DtoolsSystemClassificationEntry = {
    classification,
    groupName,
    originalSystemName:
      patch.originalSystemName !== undefined
        ? patch.originalSystemName?.trim() || null
        : prev.originalSystemName ?? null,
    customerFacingSystemName:
      patch.customerFacingSystemName !== undefined
        ? patch.customerFacingSystemName === null
          ? null
          : patch.customerFacingSystemName
        : prev.customerFacingSystemName ?? null,
  };
  return { ...map, [key]: next };
}

function normalizeWhitespaceToNullable(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

export function normalizeSystemClassificationsForSave(
  map: DtoolsSystemClassificationsMap | null | undefined,
): DtoolsSystemClassificationsMap {
  const next: DtoolsSystemClassificationsMap = {};
  for (const [key, entry] of Object.entries(map ?? {})) {
    const classification = entry.classification === "alternative" ? "alternative" : "independent";
    const normalized: DtoolsSystemClassificationEntry = {
      classification,
      groupName:
        classification === "alternative"
          ? normalizeWhitespaceToNullable(entry.groupName) ??
            resolveDefaultTabName(
              {
                customerFacingSystemName: entry.customerFacingSystemName,
                originalSystemName: entry.originalSystemName,
              },
              "",
            )
          : null,
      originalSystemName: normalizeWhitespaceToNullable(entry.originalSystemName),
      customerFacingSystemName: normalizeWhitespaceToNullable(entry.customerFacingSystemName),
    };
    next[key] = normalized;
  }
  return next;
}

export function resolveOriginalSystemName(
  entry: DtoolsSystemClassificationEntry | undefined,
  importedSystemName: string,
): string {
  return entry?.originalSystemName?.trim() || String(importedSystemName ?? "").trim();
}

export function resolveCustomerFacingSystemName(
  entry: DtoolsSystemClassificationEntry | undefined,
  importedSystemName: string,
): string {
  const custom = entry?.customerFacingSystemName?.trim();
  if (custom) return custom;
  return resolveOriginalSystemName(entry, importedSystemName);
}

/** Single customer-facing system title — never combines custom + original. */
export function resolveCustomerFacingSystemTitle(
  system: Pick<{ systemId?: string | null; name: string }, "systemId" | "name">,
  options?: {
    classification?: DtoolsSystemClassificationEntry;
    presentationSystemDisplayNames?: Record<string, string>;
    optionIndex?: number;
  },
): string {
  const key = resolveDtoolsSystemContentKey(system);
  const importedName = String(system.name ?? "").trim();
  const custom =
    options?.classification?.customerFacingSystemName?.trim() ||
    options?.presentationSystemDisplayNames?.[key]?.trim();
  if (custom) return custom;
  const original = resolveOriginalSystemName(options?.classification, importedName);
  if (original) return original;
  const optionIndex = options?.optionIndex ?? 0;
  return importedName || `Option ${optionIndex + 1}`;
}

export type StaffSystemDisplayTitle = {
  title: string;
  /** Shown below title in staff/admin views when a rename differs from the import label. */
  originalLabel?: string;
};

/** Staff/admin title with optional original import subtitle. */
export function resolveStaffSystemDisplayTitle(
  system: Pick<{ systemId?: string | null; name: string }, "systemId" | "name">,
  options?: {
    classification?: DtoolsSystemClassificationEntry;
    presentationSystemDisplayNames?: Record<string, string>;
    optionIndex?: number;
  },
): StaffSystemDisplayTitle {
  const title = resolveCustomerFacingSystemTitle(system, options);
  const original = resolveOriginalSystemName(options?.classification, system.name);
  const key = resolveDtoolsSystemContentKey(system);
  const hasCustomRename = Boolean(
    options?.classification?.customerFacingSystemName?.trim() ||
      options?.presentationSystemDisplayNames?.[key]?.trim(),
  );
  if (hasCustomRename && original && original !== title) {
    return { title, originalLabel: original };
  }
  return { title };
}

/** Keep legacy presentation.systemDisplayNames in sync for customer-facing renderers. */
export function syncPresentationSystemDisplayNamesFromClassifications(
  classifications: DtoolsSystemClassificationsMap,
  systems: Array<{ systemId?: string | null; name: string }>,
  existing: Record<string, string> = {},
): Record<string, string> {
  const next: Record<string, string> = { ...existing };
  for (const system of systems) {
    const key = resolveDtoolsSystemContentKey(system);
    const custom = classifications[key]?.customerFacingSystemName?.trim();
    if (custom) next[key] = custom;
    else delete next[key];
  }
  return next;
}

export function classificationKindLabel(kind: DtoolsSystemClassificationKind): string {
  return kind === "alternative" ? "Alternative" : "Independent";
}

export function resolveClassificationLabelForKey(
  key: string,
  options?: {
    presentationSystemDisplayNames?: Record<string, string>;
    itemSystemNames?: string[];
  },
): string {
  const displayName = options?.presentationSystemDisplayNames?.[key]?.trim();
  if (displayName) return displayName;
  const slugMatch = options?.itemSystemNames?.find(
    (name) => systemContentKey(null, name) === key || name.trim().toLowerCase().replace(/\s+/g, "-") === key,
  );
  if (slugMatch) return slugMatch;
  return key;
}

export type AlternativeGroupValidationResult = {
  valid: boolean;
  errors: string[];
};

/** When 2+ systems are classified as alternative, each must have a tab name. */
export function validateAlternativeGroupAssignments(
  systems: Array<{ systemId?: string | null; name: string }>,
  classifications: DtoolsSystemClassificationsMap,
): AlternativeGroupValidationResult {
  const alternatives = systems.filter((system) =>
    isAlternativeSystemClassification(resolveSystemClassification(classifications, system.systemId, system.name)),
  );
  if (alternatives.length < 2) return { valid: true, errors: [] };

  const errors: string[] = [];
  for (const system of alternatives) {
    const entry = resolveSystemClassification(classifications, system.systemId, system.name);
    if (!entry.groupName?.trim()) {
      errors.push(`Alternative system "${system.name}" requires a TAB NAME when multiple alternatives are present.`);
    }
  }
  return { valid: errors.length === 0, errors };
}

export function usesDtoolsV2CustomerTabModel(tabModelVersion?: DtoolsTabModelVersion | null): boolean {
  return tabModelVersion === DTOOLS_TAB_MODEL_VERSION_CLASSIFICATION;
}

function resolveSavedDtoolsSystemClassifications(input: {
  parsedMetadataJson?: unknown;
  systemClassifications?: DtoolsSystemClassificationsMap;
}): DtoolsSystemClassificationsMap {
  if (input.systemClassifications && Object.keys(input.systemClassifications).length > 0) {
    return input.systemClassifications;
  }
  return readDtoolsSystemClassifications(input.parsedMetadataJson);
}

/** Activate v2 navigation when tab model v2 is set or explicit classifications exist on the proposal. */
export function shouldUseDtoolsV2CustomerTabModel(input: {
  tabModelVersion?: DtoolsTabModelVersion | null;
  parsedMetadataJson?: unknown;
  systemClassifications?: DtoolsSystemClassificationsMap;
}): boolean {
  const savedClassifications = resolveSavedDtoolsSystemClassifications(input);
  if (Object.keys(savedClassifications).length > 0) return true;

  const version =
    input.tabModelVersion != null
      ? input.tabModelVersion
      : readDtoolsTabModelVersion(input.parsedMetadataJson);
  return version === DTOOLS_TAB_MODEL_VERSION_CLASSIFICATION;
}

/** Classifications + tab version for customer v2 rendering (ensures defaults when v2 is active). */
export function resolveDtoolsCustomerTabGate(input: {
  systems: Array<{ systemId?: string | null; name: string }>;
  tabModelVersion?: DtoolsTabModelVersion | null;
  parsedMetadataJson?: unknown;
  systemClassifications?: DtoolsSystemClassificationsMap;
}): {
  useV2TabModel: boolean;
  tabModelVersion: DtoolsTabModelVersion;
  systemClassifications: DtoolsSystemClassificationsMap;
} {
  const savedClassifications = resolveSavedDtoolsSystemClassifications(input);
  const useV2TabModel = shouldUseDtoolsV2CustomerTabModel({
    tabModelVersion: input.tabModelVersion,
    parsedMetadataJson: input.parsedMetadataJson,
    systemClassifications: savedClassifications,
  });
  const tabModelVersion = useV2TabModel
    ? DTOOLS_TAB_MODEL_VERSION_CLASSIFICATION
    : input.tabModelVersion != null
      ? input.tabModelVersion
      : readDtoolsTabModelVersion(input.parsedMetadataJson);
  const systemClassifications = useV2TabModel
    ? ensureSystemClassificationsForSystems(input.systems, savedClassifications)
    : savedClassifications;

  return { useV2TabModel, tabModelVersion, systemClassifications };
}

const CUSTOMER_VISIBLE_SYSTEM_WARNING_NAMES = ["SERVICES", "Unassigned", "Unassigned System", "Unknown System"] as const;

function normalizeSystemWarningName(name: string): string {
  return name.trim().toLowerCase();
}

function matchesCustomerVisibleSystemWarning(name: string): boolean {
  const normalized = normalizeSystemWarningName(name);
  return CUSTOMER_VISIBLE_SYSTEM_WARNING_NAMES.some(
    (watchName) => normalized === normalizeSystemWarningName(watchName),
  );
}

/** Warn staff when SERVICES, Unassigned, or other import placeholders are customer-visible. */
export function collectCustomerVisibleSystemWarnings(
  systems: Array<{ systemId?: string | null; name: string }>,
  isHidden?: (system: { systemId?: string | null; name: string }) => boolean,
): string[] {
  const warnings: string[] = [];
  for (const system of systems) {
    if (isHidden?.(system)) continue;
    if (matchesCustomerVisibleSystemWarning(system.name)) {
      warnings.push(`System '${system.name}' is customer-visible.`);
    }
  }
  return warnings;
}
